import { ICommodityTypeFormResult } from '../../models/commodity-type/interfaces';
import { ISoilLayer } from '../../models/soil-type/interfaces';
import { FertilizerNutrientViewModel, IRanchCreateFertilizerForm } from '../ranch-settings/modals/fertilizer/interfaces';
import { ILotEdit } from '../ranch-settings/modals/lot/interfaces';
import { IRanchWellViewModel } from '../ranch-settings/interfaces';
import { RanchCreation } from './interfaces';

export class RanchCreationPayload {
	public static fromParentForm(form: RanchCreation.Form.IParentForm): RanchCreation.IPayload {
		return {
			Ranch: RanchCreationPayload._mapRanch(form.RanchDetails),
			PlantingAreas: (form.PlantingAreas || []).map(RanchCreationPayload._mapPlantingArea),
			WeatherStationIds: RanchCreationPayload._mapWeatherStationIds(form.WeatherStations),
			Wells: (form.Wells || []).map(RanchCreationPayload._mapWell),
			Fertilizers: (form.Fertilizers || []).map(RanchCreationPayload._mapFertilizer),
			CommodityTypes: (form.CommodityTypes || []).map(RanchCreationPayload._mapCommodityType)
		};
	}

	private static _mapRanch(ranchDetails: RanchCreation.Form.IRanchDetails): RanchCreation.IRanch {
		return {
			Name: ranchDetails.Name,
			Acres: ranchDetails.Acres,
			Coordinates: ranchDetails.Coordinates,
			SpatialETAvailable: ranchDetails.SpatialETAvailable,
			WeatherAPI_Id: ranchDetails.WeatherAPI_Id
		};
	}

	private static _mapPlantingArea(lot: ILotEdit): RanchCreation.IPlantingArea {
		return {
			Name: lot.Name,
			Acres: lot.Acres,
			Coordinates: lot.Coordinates,
			ObstructionDepth: lot.ObstructionDepth,
			SoilLayers: RanchCreationPayload._mapSoilLayers(lot.SoilLayers)
		};
	}

	private static _mapSoilLayers(layers: ISoilLayer[]): ISoilLayer[] {
		if (!layers) {
			return [];
		}

		return layers.map((layer) => ({
			Depth: layer.Depth,
			Sand: layer.Sand,
			Silt: layer.Silt,
			Organic: layer.Organic,
			Density: layer.Density,
			Tension: layer.Tension,
			Mineralization: layer.Mineralization
		}));
	}

	private static _mapWeatherStationIds(stations: RanchCreation.Form.IWeatherStation[]): number[] {
		if (!stations) {
			return [];
		}

		return stations.map((station) => station.StationId);
	}

	private static _mapWell(well: IRanchWellViewModel): RanchCreation.IWell {
		return {
			Name: well.Name,
			ElectricalConductivity: well.ElectricalConductivity,
			NitrogenPPM: well.NitrogenPPM
		};
	}

	private static _mapFertilizer(fertilizer: IRanchCreateFertilizerForm): RanchCreation.IFertilizer {
		if (fertilizer.IsCustom) {
			return {
				IsPrivate: true,
				Name: fertilizer.Name,
				FormulationTypeId: fertilizer.FormulationTypeId,
				PoundsPerGallon: fertilizer.PoundsPerGallon,
				Nutrients: RanchCreationPayload._mapNutrients(fertilizer.Nutrients)
			};
		}

		return {
			IsPrivate: false,
			Id: fertilizer.Id
		};
	}

	private static _mapNutrients(nutrients: FertilizerNutrientViewModel[]): RanchCreation.IFertilizerNutrient[] {
		if (!nutrients) {
			return [];
		}

		return nutrients
			.filter((nutrient) => nutrient && nutrient.NutrientId != null)
			.map((nutrient) => ({
				NutrientId: nutrient.NutrientId,
				Percentage: nutrient.Percentage
			}));
	}

	private static _mapCommodityType(commodityType: ICommodityTypeFormResult): RanchCreation.ICommodityType {
		return {
			CommodityTypeId: commodityType.CommodityTypeId,
			CropTypeIds: commodityType.CropTypeIds || []
		};
	}
}
